import React, { useState } from "react";

import { Panel, Th } from "./Panel";
import { fixedDecimals, convertTime } from "../../../helpers/helpers";
import {
  displayLinesFor,
  uniqueCharges,
  findByName,
  effectiveRevisions,
} from "../writes/chargeModel";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { useTranslation } from "react-i18next";

/**
 * The snapshot tables the ledger's line-item view flattens away.
 *
 * The ledger shows the CURRENT amounts. These panels show the record behind
 * them: every revision of the billable charge, plus the other three buckets
 * the API returns. Each save appends a revision, so this is the audit trail
 * for why a total moved.
 */

const cell = (value) => (value && value !== 0 ? value : "-");

const SnapshotTable = ({ rows, shipment, showInvoiceColumn, showDate }) => {
  const { t } = useTranslation("admin");
  const lines = displayLinesFor(shipment);
  const taxes = uniqueCharges(rows, "tax_charges");
  const additional = uniqueCharges(rows, "additional_charges");

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max border-collapse tabular-nums">
        <thead>
          <tr className="border-b border-ui-border-strong bg-ui-surface-sunken">
            <Th align="center">#</Th>
            {showDate && <Th>{t("invoice.date")}</Th>}
            {showInvoiceColumn && <Th>{t("invoice.invoice")}</Th>}
            {lines.map(({ key, label }) => (
              <Th key={key} align="right">
                {t(`invoice.lines.${key}`, { defaultValue: label })}
              </Th>
            ))}
            {additional.map((charge) => (
              <Th key={charge?.NameEn} align="right">
                {charge?.NameEn}
              </Th>
            ))}
            <Th align="right">{t("invoice.subTotal")}</Th>
            {taxes.map((tax) => (
              <Th key={tax?.NameEn} align="right">
                {tax?.NameEn}
              </Th>
            ))}
            <Th align="right">{t("invoice.total")}</Th>
          </tr>
        </thead>
        <tbody className="font-manrope text-[11.5px] font-medium text-ui-text">
          {rows.map((row, index) => (
            <tr
              key={row?.id ?? index}
              className="border-b border-ui-border-subtle hover:bg-ui-row-hover"
            >
              <td className="px-[9px] py-1 text-center text-ui-text-muted">
                {index + 1}
              </td>
              {showDate && (
                <td className="whitespace-nowrap px-[9px] py-1 text-ui-text-secondary">
                  {convertTime(row?.created_at)}
                </td>
              )}
              {showInvoiceColumn && (
                <td className="px-[9px] py-1 font-semibold">
                  {row?.invoice_id ?? "-"}
                </td>
              )}
              {lines.map(({ key }) => (
                <td
                  key={key}
                  className="px-[9px] py-1 text-right text-ui-text-secondary"
                >
                  {cell(row?.[key])}
                </td>
              ))}
              {additional.map((charge) => (
                <td
                  key={charge?.NameEn}
                  className="px-[9px] py-1 text-right text-ui-text-secondary"
                >
                  {findByName(row, "additional_charges", charge?.NameEn)
                    ?.Charge ?? "-"}
                </td>
              ))}
              <td className="px-[9px] py-1 text-right font-semibold">
                {cell(row?.sub_total)}
              </td>
              {taxes.map((tax) => (
                <td
                  key={tax?.NameEn}
                  className="px-[9px] py-1 text-right text-ui-text-secondary"
                >
                  {findByName(row, "tax_charges", tax?.NameEn)?.Charge ?? "-"}
                </td>
              ))}
              <td className="px-[9px] py-1 text-right font-bold">
                {row?.total && row?.total !== 0
                  ? fixedDecimals(row?.total)
                  : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Collapsible = ({ title, chip, chipTone, hint, count, children }) => {
  const { t } = useTranslation("admin");
  const [open, setOpen] = useState(false);
  return (
    <Panel
      title={title}
      chip={chip}
      chipTone={chipTone}
      hint={hint}
      actions={
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 rounded border border-ui-border bg-ui-surface px-2 py-1 font-manrope text-[10px] font-semibold text-ui-text-muted hover:bg-ui-surface-sunken"
        >
          {open ? t("invoice.hide") : t("invoice.showWhat", { what: count })}
          {open ? (
            <MdExpandLess className="h-4 w-4" />
          ) : (
            <MdExpandMore className="h-4 w-4" />
          )}
        </button>
      }
    >
      {open && children}
    </Panel>
  );
};

const ChargeHistoryPanels = ({ data }) => {
  const { t } = useTranslation("admin");
  // A Modify echo that reuses its row's id replaces that row rather than
  // listing it twice — the same rows a re-fetch returns. Unique ids (every
  // shipment without a Modify) pass through unchanged.
  const billed = effectiveRevisions(data?.shipment_charges).list;
  const quote = data?.quoteCharges || [];
  const estimate = data?.estCharges || [];
  const carrier = data?.billingAutomation || [];

  return (
    <div className="flex flex-col gap-2">
      {billed.length > 0 && (
        <Collapsible
          title={t("invoice.revisionHistory")}
          chip={t("invoice.revisionCount", { count: billed.length })}
          hint={t("invoice.revisionHistoryHint")}
          count={t("invoice.revisionsShown", { count: billed.length })}
        >
          <SnapshotTable rows={billed} shipment={data} showDate />
        </Collapsible>
      )}

      {estimate.length > 0 && (
        <Collapsible
          title={t("invoice.estimatedPrice")}
          chip={t("invoice.agreedAtBooking")}
          hint={t("invoice.estimatedPriceHint")}
          count={t("invoice.estimateShown")}
        >
          <SnapshotTable rows={estimate} shipment={data} />
        </Collapsible>
      )}

      {quote.length > 0 && (
        <Collapsible
          title={t("invoice.preMarkupFigures")}
          chip={t("invoice.derived")}
          hint={t("invoice.preMarkupHint")}
          count={t("invoice.preMarkupShown")}
        >
          <SnapshotTable rows={quote} shipment={data} />
        </Collapsible>
      )}

      {/* Rows carrying a real carrier invoice_id. Structurally separate and
          never editable — an admin must not be able to change a charge that is
          already on a carrier invoice. */}
      {carrier.length > 0 && (
        <Collapsible
          title={t("invoice.carrierBill")}
          chip={t("invoice.alreadyInvoiced")}
          chipTone="warning"
          hint={t("invoice.carrierBillHint")}
          count={t("invoice.linesShown", { count: carrier.length })}
        >
          <SnapshotTable rows={carrier} shipment={data} showInvoiceColumn />
        </Collapsible>
      )}
    </div>
  );
};

export default ChargeHistoryPanels;
